<template lang='pug'>
  div(v-if='isArticlesLoading')
    .articles-loader
      .loader-spinner

  div(v-else)
    .beauty-feed-index
      .row
        .col-12(:class='isMobile ? null : "justify-content-center"')
          .content-type-header(v-if='articles.length === 0')
            h1.title(v-text='$t("errorMessage.noArticleTitle")')
            p.text-snippet(v-text='$t("errorMessage.noArticleDescription")')
          .content-type-header(v-else)
            h1.title(v-if='selectedTag.length !== 0')
              = 'Tag: {{ selectedTag }}'
            div(v-else)
              h1.title(v-if='selectedFilter !== "All"')
                = '{{ capitalizeSelectedTab }} > {{ capitalizeSelectedFilter }}'
              h1.title(v-else)
                = '{{ capitalizeSelectedTab }} > {{ capitalizeSelectedFilter }}'
      div(v-if='articles.length !== 0')
        .row.justify-content-between
          article-card(v-for='article, index in articles' :key='index' v-bind='article' class='col-sm-6 card-margin')
        .justify-content-center.mb-4
          pagination(
            :startPage='currentPage'
            :pageCount='pageCount'
            :full='true'
            @setPage='updateArticles'
            scrollTo='#top-beauty-feed'
          )
</template>

<script>
import Pagination from '../shared/pagination.vue'
import ArticleCard from './article/card.vue'
import { fullUrl } from '@/modules/services/utilities'
import isEmpty from 'lodash/isEmpty'
import { createNamespacedHelpers } from 'vuex'
import {
  getAllArticlesByFilters,
  getArticleFilters,
  getContentTypes,
  getTags
} from '@/modules/content-platform-api'
import {
  formatArticlesForFilter as formatArticles,
  formatArticleFilters,
  formatContentTypes
} from './helpers'
import { dynamicLoadStore } from '@/mixins/store-util'
import beautyFeed from '@/store/modules/beauty-feed'

const { mapState } = createNamespacedHelpers('beautyFeed')

function capitalizeFirstLetter (string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const getIndexArticles = async (store, page, options, route = {}) => {
  store.commit('beautyFeed/setIsArticlesLoading', true)
  if (options.articleFilters && options.articleFilters === 'All') {
    delete options['articleFilters']
  }
  const { data: { data: articles, meta, included } } =
  await getAllArticlesByFilters(
    I18n.acceptLanguage,
    { page: page,
      perPage: 10,
      filter: options
    }
  )

  let articleContentTypes = []
  if (!isEmpty(articles)) {
    articleContentTypes = included.filter(contentType => {
      return contentType.type === 'content_types'
    })
  }

  let articleTags = {}
  if (!isEmpty(articles) && options.tags) {
    articleTags = included.filter(tag => {
      return tag.type === 'tags' &&
        tag.attributes.name === options.tags
    })
  }

  let articleFilters = {}
  if (!isEmpty(articles) && options.articleFilters) {
    articleFilters = included.filter(articleFilter => {
      return articleFilter.type === 'article_filters' &&
        articleFilter.attributes.name === options.articleFilters
    })
  }

  if (options.setSelectedTab && options.contentTypes) {
    store.commit('beautyFeed/setSelectedTab', options.contentTypes)
  }

  if (options.setSelectedFilter && options.articleFilters) {
    store.commit('beautyFeed/setSelectedFilter', options.articleFilters)
  }

  store.commit('beautyFeed/setArticles', formatArticles(articles, articleContentTypes, articleTags, articleFilters))
  store.commit('beautyFeed/setArticleCount', meta.total)
  store.commit('beautyFeed/setPageCount', meta['total-pages'])
  store.commit('beautyFeed/setIsArticlesLoading', false)

  if (route.name === 'beautyfeed-nested-filter') {
    let seoTitle = options.contentTypes
    if (options.articleFilters) {
      seoTitle += ` | ${options.articleFilters}`
    }
    store.commit('setSeoMetaTitle', seoTitle)
    store.commit('setSeoMetaDescription', getSeoFilterDesc(options.articleFilters))
  } else if (route.name === 'beautyfeed-filter') {
    let seoTitle = 'Beautyfeed'
    if (options.articleFilters) {
      seoTitle += ` | ${options.articleFilters}`
    }
    store.commit('setSeoMetaTitle', seoTitle)
    store.commit('setSeoMetaDescription', getSeoFilterDesc(options.articleFilters))
  } else {
    store.commit('setSeoMetaTitle', options.tags)
    store.commit('setSeoMetaDescription', getSeoDesc(options.tags))
  }
  store.commit('setSeoOgUrl', fullUrl(route.path))
  store.commit('setSeoUpdate', true)
}

const getSeoFilterDesc = (filter) => {
  if (filter) {
    return `Stay up to date on everything ${filter} from the latest ${filter} related tips and tricks to exclusive sneak peeks from your favourite makeup brands right here!`
  } else {
    return 'Stay up to date on everything beauty from the latest beauty-related tips and tricks to exclusive sneak peeks from your favourite beauty brands right here!'
  }
}

const getSeoDesc = (selectedTag) => {
  return `Stay up to date on everything about ${selectedTag} from the latest tips and tricks to exclusive sneak peeks from your favourite brands here!`
}

export default {
  components: {
    'pagination': Pagination,
    ArticleCard
  },
  mixins: [
    dynamicLoadStore({ beautyFeed })
  ],
  props: {
    nested: { type: Boolean, default: false }
  },
  computed: {
    ...mapState([
      'articles', 'articleFilters', 'contentTypes',
      'selectedTab', 'selectedTag', 'selectedFilter',
      'currentPage', 'pageCount', 'isArticlesLoading'
    ]),
    capitalizeSelectedTab () {
      return capitalizeFirstLetter(this.selectedTab)
    },
    capitalizeSelectedFilter () {
      return capitalizeFirstLetter(this.selectedFilter)
    },
    isMobile () {
      return vueSupport.isMobile
    }
  },
  watch: {
    '$route': 'refetchArticles'
  },
  created () {
    this.fetchArticles()
  },
  methods: {
    refetchArticles () {
      // proxy to avoid passing router object as page
      this.fetchArticles()
    },
    updateArticles (page) {
      this.$store.commit('beautyFeed/setCurrentPage', page)

      let route = { path: this.$route.path }
      if (page > 1) {
        route = Object.assign(route, {
          query: { page: page }
        })
      }
      this.$router.replace(route)

      if (!isEmpty(this.selectedTag)) {
        getIndexArticles(this.$store, page, { tags: this.selectedTag })
      } else if (this.selectedTab !== 'Home' && !isEmpty(this.selectedFilter)) {
        getIndexArticles(this.$store, page, {
          contentTypes: this.selectedTab,
          articleFilters: this.selectedFilter,
          setSelectedFilter: true,
          setSelectedTab: true
        }, this.$route)
      } else {
        getIndexArticles(this.$store, page, {
          articleFilters: this.selectedFilter,
          setSelectedFilter: true
        }, this.$route)
      }
    },
    getContentType () {
      return this.contentTypes.find(contentType => contentType.slug === this.$route.params.contentType)
    },
    getArticleFilter () {
      return this.articleFilters.find(filter => filter.slug === this.$route.params.filter)
    },
    beautyfeedPageNotAvailable (path, msg) {
      this.$router.push(path)
      this.$store.commit('setNotification', {
        type: 'error',
        msg: msg
      })
    },
    async fetchArticles (page = 1) {
      if (this.$route.query.page) {
        page = parseInt(this.$route.query.page)
      }
      this.$store.commit('beautyFeed/setCurrentPage', page)

      const { data: { data: articleFilters } } = await getArticleFilters(I18n.acceptLanguage)
      this.$store.commit('beautyFeed/setArticleFilters', formatArticleFilters(articleFilters))

      const { data: { data: contentTypes } } = await getContentTypes(I18n.acceptLanguage)
      this.$store.commit('beautyFeed/setContentTypes', formatContentTypes(contentTypes))

      if (this.$route.name === 'beautyfeed-tag') {
        if (isEmpty(this.selectedTag)) {
          const { data: { data: tags } } = await getTags(I18n.acceptLanguage)
          const tag = tags.find(tag => tag.attributes.slug === this.$route.params.slug)
          if (!tag) {
            return this.beautyfeedPageNotAvailable('/beautyfeed', this.$t('errorMessage.tagNotAvailable'))
          }
          this.$store.commit('beautyFeed/setSelectedTag', tag.attributes.name)
        }
        getIndexArticles(this.$store, page, { tags: this.selectedTag }, this.$route)
        this.$store.commit('beautyFeed/setSelectedTab', 'Home')
        this.$store.commit('beautyFeed/setSelectedFilter', 'All')
      }

      const articleFilter = this.getArticleFilter()
      if (this.$route.name === 'beautyfeed-filter') {
        if (!articleFilter) {
          return this.beautyfeedPageNotAvailable('/beautyfeed', this.$t('errorMessage.pageNotAvailable'))
        }
        getIndexArticles(this.$store, page, {
          articleFilters: articleFilter.name, setSelectedFilter: true
        }, this.$route)
        this.$store.commit('beautyFeed/setSelectedTab', 'Home')
      }
      if (this.$route.name === 'beautyfeed-nested-filter') {
        const contentType = this.getContentType()
        if (!contentType) {
          return this.beautyfeedPageNotAvailable('/beautyfeed', this.$t('errorMessage.pageNotAvailable'))
        }
        if (!articleFilter) {
          return this.beautyfeedPageNotAvailable(`/beautyfeed/${this.$route.params.contentType}`, this.$t('errorMessage.pageNotAvailable'))
        }
        getIndexArticles(this.$store, page, {
          articleFilters: articleFilter.name, contentTypes: contentType.name, setSelectedTab: true, setSelectedFilter: true
        }, this.$route)
      }
    }
  }
}
</script>

<style lang='scss'>
  .card-margin {
    margin-bottom: 40px;
  }

  .content-type-header {
    .title {
      width: 100%;
      font-size: 24px;
      text-align: center;
      line-height: 29px;

      margin-bottom: 8px;

      @media (min-width: 768px) {
        padding: 0;
      }
    }

    .text-snippet {
      text-align :center;
      word-wrap: break-word;
      margin-bottom: 40px;
      color: $grey-dark;
    }

    @media (max-width: 768px) {
      padding: 0 10px;

      .title, .text-snippet {
        text-align: left;
      }

      .text-snippet {
        margin-bottom: 0;
      }
    }
  }

  .articles-loader {
    display: flex;
    justify-content: center;
    min-height: 900px;
    padding-top: 40px;
  }

  .beauty-feed-index {
    .row {
      margin-bottom: 40px;
    }
    @media (max-width: 768px) {
      .row {
        margin-bottom: 0;
      }
    }
  }

  .justify-content-between {
    display: flex;
    justify-content: space-between;
  }

  .justify-content-center {
    display: flex;
    justify-content: center;
  }

  .mb-4 {
    margin-bottom: 2rem;
  }

  @media (max-width: 768px) {
    .mb-sm {
      margin-bottom: 29px;
    }
  }
</style>
